import { useRef, useState } from "react"
import { toast } from "react-toastify"

import { Button } from "~/ui-rtk/components/ui/controls"
import {
  useConfirmExperimentUploadMutation,
  usePresignExperimentUploadMutation,
  type ExperimentUploadKind,
} from "~/ui-rtk/api/experimentApi"

const CSV_CONTENT_TYPE = "text/csv"

type ExperimentUploadButtonProps = {
  experimentId: string
  kind: ExperimentUploadKind
  label: string
}

/**
 * Two-phase CSV upload for wholesale / first-party-retail sales data:
 * presign → PUT to S3 → confirm. The Content-Type must match what the URL
 * was signed with or S3 rejects the PUT.
 */
export default function ExperimentUploadButton({
  experimentId,
  kind,
  label,
}: ExperimentUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [presignUpload] = usePresignExperimentUploadMutation()
  const [confirmUpload] = useConfirmExperimentUploadMutation()

  const handleFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only .csv files are supported")
      return
    }

    setIsUploading(true)
    try {
      const presigned = await presignUpload({
        id: experimentId,
        body: { kind, fileName: file.name, fileType: CSV_CONTENT_TYPE },
      }).unwrap()

      const putResponse = await fetch(presigned.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": CSV_CONTENT_TYPE },
        body: file,
      })
      if (!putResponse.ok) {
        throw new Error(`S3 upload failed (${putResponse.status})`)
      }

      await confirmUpload({
        id: experimentId,
        body: { kind, fileName: file.name, s3Key: presigned.s3Key },
      }).unwrap()

      toast.success(`Uploaded ${file.name}`)
    } catch (err) {
      console.error("Experiment CSV upload failed:", err)
      toast.error("Upload failed. Please try again.")
    } finally {
      setIsUploading(false)
      if (inputRef.current) {
        inputRef.current.value = ""
      }
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={event => {
          const file = event.target.files?.[0]
          if (file) {
            void handleFile(file)
          }
        }}
      />
      <Button
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
        className="p-2 px-4 text-sm bg-system-blue-600 text-white"
      >
        <p className="text-sm">{isUploading ? "Uploading..." : label}</p>
      </Button>
    </>
  )
}
